import { supabase } from '@/lib/supabase';

export const ORG_ID = '00000000-0000-0000-0000-000000000001';

export interface Article {
  id: string;
  reference: string | null;
  kind: string;
  libelle: string;
  description: string | null;
  categorie_id: string | null;
  unite_id: string;
  taux_tva_id: string;
  prix_achat_ht: string | null;
  prix_vente_ht: string;
  actif: boolean;
}

export interface Unite {
  id: string;
  code: string;
  libelle: string;
}
export interface TauxTva {
  id: string;
  code: string;
  libelle: string;
  taux: string;
}
export interface Categorie {
  id: string;
  nom: string;
}

export interface ArticleInput {
  reference: string | null;
  kind: string;
  libelle: string;
  description: string;
  categorie_id: string | null;
  unite_id: string;
  taux_tva_id: string;
  prix_vente_ht: string;
  actif: boolean;
}

export const ARTICLE_KIND_LABELS: Record<string, string> = {
  materiau: 'Matériau',
  prestation: 'Prestation',
  main_oeuvre: "Main d'œuvre",
  forfait: 'Forfait',
  sous_traitance: 'Sous-traitance',
};

export function formatEuro(value: string | number | null): string {
  const n = Number(value ?? 0);
  return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(
    Number.isFinite(n) ? n : 0,
  );
}

export function formatTauxPct(taux: string): string {
  return `${(Number(taux) * 100).toLocaleString('fr-FR')} %`;
}

export async function listArticles(): Promise<Article[]> {
  const { data, error } = await supabase.from('articles').select('*').order('libelle');
  if (error) throw new Error(error.message);
  return (data ?? []) as Article[];
}

export async function getArticle(id: string): Promise<Article> {
  const { data, error } = await supabase.from('articles').select('*').eq('id', id).single();
  if (error) throw new Error(error.message);
  return data as Article;
}

export async function listUnites(): Promise<Unite[]> {
  const { data, error } = await supabase.from('unites').select('id,code,libelle').order('ordre');
  if (error) throw new Error(error.message);
  return (data ?? []) as Unite[];
}

export async function listTauxTva(): Promise<TauxTva[]> {
  const { data, error } = await supabase.from('taux_tva').select('id,code,libelle,taux').order('code');
  if (error) throw new Error(error.message);
  return (data ?? []) as TauxTva[];
}

export async function listCategories(): Promise<Categorie[]> {
  const { data, error } = await supabase.from('categories_article').select('id,nom').order('ordre');
  if (error) throw new Error(error.message);
  return (data ?? []) as Categorie[];
}

export async function createArticle(input: ArticleInput): Promise<void> {
  const { error } = await supabase.from('articles').insert({ ...input, organization_id: ORG_ID });
  if (error) throw new Error(error.message);
}

export async function updateArticle(id: string, input: ArticleInput): Promise<void> {
  const { error } = await supabase.from('articles').update(input).eq('id', id);
  if (error) throw new Error(error.message);
}

export async function deleteArticle(id: string): Promise<void> {
  const { error } = await supabase.from('articles').delete().eq('id', id);
  // Les lignes de devis référençant l'article passent automatiquement à NULL
  // (FK on delete set null) tout en conservant leur libellé/prix figés.
  if (error) throw new Error(error.message);
}
